import type { Category, ScoringSex } from './types';
import { lbToKg, calculateDotsCoefficient } from './dots';

export const CATEGORY_BENCHMARKS_LB: Record<Category, number> = {
  push: 215,
  row: 190,
  hinge: 325,
  squat: 275,
};

export const REFERENCE_BODY_WEIGHT_LB = 200;

/**
 * Category-specific sex adjustment multipliers.
 * Standard DOTS was designed for 3-lift Total (Squat + Bench + Deadlift combined).
 * In powerlifting world-record data, female-to-male strength ratio is:
 * - Squat & Deadlift: ~68%
 * - Bench Press: ~56%
 *
 * Because SWOL evaluates Push as an isolated category, standard DOTS under-rewards
 * female pressing relative to leg/back pulling. Applying a 1.14x correction factor (56% -> 68%)
 * makes female Push 100% fair and balanced against Squat and Pull.
 */
export const SEX_CATEGORY_ADJUSTMENT: Record<ScoringSex, Record<Category, number>> = {
  male: {
    push: 1.0,
    row: 1.0,
    hinge: 1.0,
    squat: 1.0,
  },
  female: {
    // Upper-body pulling sits between pressing (~56%) and lower body (~68%),
    // so it needs a smaller correction than Push. Starting estimate - tune.
    push: 1.14,
    row: 1.08,
    hinge: 1.0,
    squat: 1.0,
  },
};

/**
 * Calculates the reference DOTS value for a given category (200lb male benchmark).
 *
 * @param category The lift category (push, pull, squat).
 * @returns The reference DOTS value.
 */
export function calculateReferenceDots(category: Category): number {
  const referenceLiftKg = lbToKg(CATEGORY_BENCHMARKS_LB[category]);
  const referenceDotsCoeff = calculateDotsCoefficient(REFERENCE_BODY_WEIGHT_LB, 'male');
  return referenceLiftKg * referenceDotsCoeff;
}

/**
 * Calculates the points for a normalized lift.
 * Points formula: (normalizedLiftKg * dotsCoeff(user)) / (referenceLiftKg * dotsCoeff(200lb male)) * 100 * adjustment
 *
 * @param normalizedLiftLb The normalized 1RM lift in pounds.
 * @param bodyWeightLb The lifter's body weight in pounds.
 * @param sex The lifter's biological sex.
 * @param category The lift category (push, pull, squat).
 * @returns The points awarded for the lift.
 */
export function calculateCategoryPoints(
  normalizedLiftLb: number,
  bodyWeightLb: number,
  sex: ScoringSex,
  category: Category,
): number {
  const normalizedLiftKg = lbToKg(normalizedLiftLb);
  const userDotsCoeff = calculateDotsCoefficient(bodyWeightLb, sex);
  const referenceDots = calculateReferenceDots(category);
  const adjustment = SEX_CATEGORY_ADJUSTMENT[sex][category];

  return ((normalizedLiftKg * userDotsCoeff) / referenceDots) * 100 * adjustment;
}

/**
 * Inverse of calculateCategoryPoints: the normalized (competition-lift) 1RM that
 * would earn this many points for this athlete.
 *
 * Used to express a pull-up as a barbell-row equivalent, so a pull-up 1RM and a
 * bent-over row 1RM can be compared directly in the same category.
 */
export function normalizedLiftForPoints(
  points: number,
  bodyWeightLb: number,
  sex: ScoringSex,
  category: Category,
): number {
  const userDotsCoeff = calculateDotsCoefficient(bodyWeightLb, sex);
  const referenceDots = calculateReferenceDots(category);
  const adjustment = SEX_CATEGORY_ADJUSTMENT[sex][category];
  const normalizedLiftKg = (points * referenceDots) / (100 * adjustment * userDotsCoeff);
  return normalizedLiftKg / 0.45359237;
}
